import { IOption } from '../common/common.types'
import { IDefaultAgeGroup, IDefaults } from '../features/defaults/store/defaults.types'
import { calculateAge, isValidZollNowDate, isValidZollTime } from './dateTime.util'
import { getAge } from './showAssessmentAirwayFields.util'

export const isDateTimeLessThanOrEqualToPTA = (
  ptaDate: string,
  ptaTime: string,
  procedureDate: string,
  procedureTime: string,
  displaySeconds: boolean
) => {
  const ptaDateTime = new Date(ptaDate)
  if (ptaTime && isValidZollTime(ptaTime)) {
    if (displaySeconds && ptaTime.split(':').length >= 3) {
      ptaDateTime.setSeconds(+ptaTime.split(':')[2])
    }
    ptaDateTime.setMinutes(+ptaTime.split(':')[1])
    ptaDateTime.setHours(+ptaTime.split(':')[0])
  }
  const procDateTime = new Date(procedureDate || 0)
  if (procedureTime && isValidZollTime(procedureTime)) {
    if (displaySeconds && procedureTime.split(':').length >= 3) {
      procDateTime.setSeconds(+procedureTime.split(':')[2])
    }
    procDateTime.setMinutes(+procedureTime.split(':')[1])
    procDateTime.setHours(+procedureTime.split(':')[0])
  }
  return procDateTime <= ptaDateTime
}

export const isDateTimeLessThanPTA = (
  ptaDate: string,
  ptaTime: string,
  procedureDate: string,
  procedureTime: string,
  displaySeconds: boolean
) => {
  const ptaDateTime = new Date(ptaDate)
  if (ptaTime && isValidZollTime(ptaTime)) {
    if (displaySeconds && ptaTime.split(':').length >= 3) {
      ptaDateTime.setSeconds(+ptaTime.split(':')[2])
    }
    ptaDateTime.setMinutes(+ptaTime.split(':')[1])
    ptaDateTime.setHours(+ptaTime.split(':')[0])
  }
  const procDateTime = new Date(procedureDate || 0)
  if (procedureTime && isValidZollTime(procedureTime)) {
    if (displaySeconds && procedureTime.split(':').length >= 3) {
      procDateTime.setSeconds(+procedureTime.split(':')[2])
    }
    procDateTime.setMinutes(+procedureTime.split(':')[1])
    procDateTime.setHours(+procedureTime.split(':')[0])
  }
  return procDateTime < ptaDateTime
}

export const isProcDateTimePTA = (
  atPatientDate: string,
  atPatientTime: string,
  onSceneDate: string,
  onSceneTime: string,
  procedureDate: string,
  procDateTime: string,
  displaySeconds: boolean
) => {
  if (atPatientDate && isValidZollNowDate(atPatientDate)) {
    return isDateTimeLessThanPTA(atPatientDate, atPatientTime, procedureDate, procDateTime, displaySeconds)
  } else if (onSceneDate && isValidZollNowDate(onSceneDate)) {
    return isDateTimeLessThanPTA(onSceneDate, onSceneTime, procedureDate, procDateTime, displaySeconds)
  }
}

export const getPTADateTimeErrorMessage = (
  atPatientDate: string,
  atPatientTime: string,
  onSceneDate: string,
  onSceneTime: string,
  procedureDate: string,
  procDateTime: string,
  displaySeconds: boolean
) => {
  if (atPatientDate && isValidZollNowDate(atPatientDate)) {
    if (!isDateTimeLessThanOrEqualToPTA(atPatientDate, atPatientTime, procedureDate, procDateTime, displaySeconds))
      return `PTA entries cannot be selected after the At Patient time of the chart ${atPatientDate} ${atPatientTime}`
  } else if (onSceneDate && isValidZollNowDate(onSceneDate)) {
    if (!isDateTimeLessThanOrEqualToPTA(onSceneDate, onSceneTime, procedureDate, procDateTime, displaySeconds))
      return `PTA entries cannot be selected after the On Scene time of the chart ${onSceneDate} ${onSceneTime}`
  }
}

export enum AgeRangeConstants {
  ADULT = 1,
  ADOLESCENT = 2,
  CHILD = 3,
  INFANT = 4,
  DEFAULT = 1,
}

export const getAgeRangeGroupId = (
  patientDob: string | null,
  patientAge: string | null,
  ageUnit: IOption | any | null
): number => {
  let years = NaN

  if (patientDob) {
    const age = calculateAge(patientDob)
    years = age.years
  } else {
    if (patientAge && ageUnit) {
      years = getAge(Number(patientAge), ageUnit.id?.toString())
    }
  }

  if (isNaN(years)) {
    return AgeRangeConstants.DEFAULT.valueOf()
  }

  if (years >= 18) {
    return AgeRangeConstants.ADULT.valueOf()
  }

  if (years >= 12) {
    return AgeRangeConstants.ADOLESCENT.valueOf()
  }

  if (years >= 2) {
    return AgeRangeConstants.CHILD.valueOf()
  }

  return AgeRangeConstants.INFANT.valueOf()
}

export const getDefaultsForAgeRange = (
  defaults: IDefaults,
  procedureId: string,
  ageRange: number
): IDefaultAgeGroup | null => {
  const procedureDefaults = defaults.find((procedureDefault) => {
    return procedureDefault.procedureId === procedureId
  })

  if (!procedureDefaults) {
    return null
  }

  const defaultsForAgeRange = procedureDefaults.ageGroups.find((ageRangeDefault) => {
    return ageRangeDefault.ageRangeId === ageRange
  })

  if (!defaultsForAgeRange) {
    return null
  }

  return defaultsForAgeRange
}
